import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  CommonModule,
} from '@angular/common';

import {
  Router,
  RouterModule,
} from '@angular/router';

import {
  TranslateModule,
} from '@ngx-translate/core';

import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';


import {
  AdminService,
} from 'src/app/services/admin/admin.service';

import {
  AppService,
} from 'src/app/services/app/app.service';

import {
  LanguagesService,
} from 'src/app/services/languages/languages.service';

import {
  HeaderAuthComponent,
} from 'src/app/components/layout/header-auth/header-auth.component';

import {
  CreateUpdateModalComponent,
} from 'src/app/components/layout/create-update-modal/create-update-modal.component';


interface SignupRequest {
  name: string;
  lastname: string;
  email: string;
  password: string;
  check: boolean;
  lang: string;

  restaurant_name: string;
  business_type: string;
  city: string;
  address: string;
  subdomain: string;


}


@Component({
  selector: 'app-sign-up',

  standalone: true,

  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    HeaderAuthComponent,
  ],

  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.css'],
})
export class SignUpComponent implements OnInit, OnDestroy {

  validateForm!: FormGroup;

  loading = false;
  checkingDomain = false;

  showPassword = false;
  isTyping = false;
  passwordStrength = 0;

  passwordErrors: string[] = [];

  isDomainValid = false;
  isDomainTaken = false;
  domainChecked = false;

  error: Record<string, string[]> = {};

  generalError = '';

  selectedLanguage = '';

  currentStep = 1;

  private readonly destroy$ = new Subject<void>();


  constructor(
    private readonly formBuilder: FormBuilder,
    public readonly AuthService: AdminService,
    private readonly AppService: AppService,
    private readonly languageService: LanguagesService,
    private readonly router: Router,
  ) {
    if (this.AuthService.isAdmin()) {
      void this.router.navigate(['/']);
    }

    this.languageService.selectedLanguage$
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe((language: any) => {
        this.selectedLanguage = language?.code ?? '';
      });
  }


  ngOnInit(): void {
    this.initializeForm();
    this.subscribeToPasswordChanges();
    this.subscribeToDomainChanges();
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get stepProgress(): number {
    return (this.currentStep / 3) * 100;
  }

  get passwordValue(): string {
    return this.validateForm
      ?.get('password')
      ?.value ?? '';
  }


  get hasMinimumPasswordLength(): boolean {
    return this.passwordValue.length >= 8;
  }


  get hasLowercaseLetter(): boolean {
    return /[a-z]/.test(this.passwordValue);
  }


  get hasPasswordNumber(): boolean {
    return /\d/.test(this.passwordValue);
  }


  private initializeForm(): void {
    this.validateForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],

      lastname: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],

      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.maxLength(150),
        ],
      ],

      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/.*[a-z].*/),
          Validators.pattern(/.*\d.*/),
        ],
      ],

      restaurant_name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(150),
        ],
      ],

      business_type: [
        '',
        [
          Validators.required,
        ],
      ],

      city: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],

      address: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
        ],
      ],

      sub_domain: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(63),
          Validators.pattern(
            /^(?!-)(?!.*--)[a-z0-9-]+(?<!-)$/,
          ),
        ],
      ],

      check: [
        false,
        Validators.requiredTrue,
      ],
    });
  }


  private subscribeToPasswordChanges(): void {
    this.validateForm
      .get('password')
      ?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe((value: string | null) => {
        const password = value ?? '';

        this.isTyping = password.length > 0;

        this.calculatePasswordStrength(password);

        this.passwordErrors = [];

        this.removeApiError('password');
      });
  }


  private subscribeToDomainChanges(): void {
    this.validateForm
      .get('sub_domain')
      ?.valueChanges
      .pipe(
        debounceTime(550),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value: string | null) => {
        const normalizedDomain =
          this.normalizeSubDomain(value ?? '');

        if (
          normalizedDomain !==
          this.validateForm.get('sub_domain')?.value
        ) {
          this.validateForm
            .get('sub_domain')
            ?.setValue(
              normalizedDomain,
              {
                emitEvent: false,
              },
            );
        }

        this.resetDomainState();

        if (!normalizedDomain) {
          return;
        }

        this.isDomainValid =
          this.isSubDomainFormatValid(normalizedDomain);

        if (!this.isDomainValid) {
          this.domainChecked = true;
          return;
        }

        this.checkDomainAvailability(normalizedDomain);
      });
  }

  nextStep(): void {
    this.generalError = '';

    if (this.currentStep === 1) {
      const stepOneControls = [
        'name',
        'lastname',
        'email',
        'password',
      ];

      if (!this.validateStep(stepOneControls)) {
        this.passwordErrors = this.getLocalPasswordErrors();
        return;
      }

      this.currentStep = 2;
      this.scrollToFormTop();
      return;
    }

    if (this.currentStep === 2) {
      const stepTwoControls = [
        'restaurant_name',
        'business_type',
        'city',
        'address',
      ];

      if (!this.validateStep(stepTwoControls)) {
        return;
      }

      this.currentStep = 3;
      this.scrollToFormTop();
    }
  }


  previousStep(): void {
    if (this.loading || this.currentStep <= 1) {
      return;
    }

    this.currentStep -= 1;
    this.generalError = '';
    this.scrollToFormTop();
  }


  private validateStep(controlNames: string[]): boolean {
    controlNames.forEach((controlName: string) => {
      const control = this.validateForm.get(controlName);

      control?.markAsTouched();
      control?.markAsDirty();
      control?.updateValueAndValidity();
    });

    return controlNames.every(
      (controlName: string) =>
        this.validateForm.get(controlName)?.valid === true,
    );
  }


  private scrollToFormTop(): void {
    window.setTimeout(() => {
      document
        .querySelector('.ml-register-card')
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
    }, 50);
  }


  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }


  onSubDomainInput(): void {
    this.generalError = '';
    this.removeApiError('sub_domain');
  }


  isControlInvalid(controlName: string): boolean {
    const control =
      this.validateForm.get(controlName);

    return Boolean(
      control &&
      control.invalid &&
      (
        control.touched ||
        control.dirty
      ),
    );
  }


  submitForm(): void {

    this.generalError = '';
    this.error = {};
    this.passwordErrors = [];
      if (

      !this.validateStep([
        'name',
        'lastname',
        'email',
        'password',
      ])
      ) {
        this.currentStep = 1;
        this.passwordErrors = this.getLocalPasswordErrors();
        this.scrollToFormTop();
        return;
      }

    if (
      !this.validateStep([
        'restaurant_name',
        'business_type',
        'city',
        'address',
      ])

    ) {
      this.currentStep = 2;
      this.scrollToFormTop();
      return;
    }
    if (
      !this.validateStep([
        'sub_domain',
        'check',
      ])
    ) {
      this.currentStep = 3;
      this.scrollToFormTop();
      return;
    }

    this.validateForm.markAllAsTouched();

    if (this.validateForm.invalid) {
      this.passwordErrors =
        this.getLocalPasswordErrors();

      this.scrollToFirstInvalidControl();

      return;
    }

    if (this.checkingDomain) {
      this.generalError =
        'დაელოდე დომენის შემოწმების დასრულებას.';

      return;
    }

    if (
      !this.domainChecked ||
      !this.isDomainValid ||
      this.isDomainTaken
    ) {
      this.generalError =
        'აირჩიე ხელმისაწვდომი დომენის სახელი.';

      this.validateForm
        .get('sub_domain')
        ?.markAsTouched();

      return;
    }

    const formValue = this.validateForm.getRawValue();
    const accountData: SignupRequest = {
      name: formValue.name,
      lastname: formValue.lastname,

      email: formValue.email,
      password: formValue.password,

      check: formValue.check,
      lang: this.selectedLanguage || 'ka',

      restaurant_name: formValue.restaurant_name,
      business_type: formValue.business_type,
      city: formValue.city,
      address: formValue.address,
      subdomain: formValue.sub_domain,

    };

    this.loading = true;

    this.AuthService
      .signUp(accountData)
      .subscribe({
        next: (response: any) => {
          this.loading = false;

          if (response?.token) {
            this.AuthService.setTokens(response.token);
          }

          if (response?.user) {
            localStorage.setItem(
              '@mlRegisteredUser',
              JSON.stringify(response.user),
            );
          }

          if (response?.app) {
            localStorage.setItem(
              '@mlCurrentApp',
              JSON.stringify(response.app),
            );
          }

          void this.router.navigate(
            ['/pricing'],
            {
              queryParams: {
                registered: 'true',
                app_id: response?.app?.id,
              },
            },
          );
        },

        error: (apiError: any) => {
          this.loading = false;

          this.handleSignupError(apiError);
        },
      });
  }


  private checkDomainAvailability(
    subDomain: string,
  ): void {
    this.checkingDomain = true;
    this.domainChecked = false;
    this.isDomainTaken = false;
    this.generalError = '';

    this.AppService
      .checkDoamin(subDomain)
      .subscribe({
        next: (response: any) => {
          this.checkingDomain = false;
          this.domainChecked = true;

          /*
           * თუ შენს API-ში app.domain_ok ნიშნავს,
           * რომ დომენი დაკავებულია, ეს ლოგიკა დატოვე.
           */
          if (
            response?.message ===
            'app.domain_ok'
          ) {
            this.isDomainTaken = true;
            this.isDomainValid = true;

            return;
          }

          this.isDomainTaken = false;
          this.isDomainValid = true;
        },

        error: (apiError: any) => {
          this.checkingDomain = false;
          this.domainChecked = true;
          this.isDomainValid = false;
          this.isDomainTaken = false;

          if (
            apiError?.error?.message ===
            'app.payment_required'
          ) {
            this.generalError =
              'წინა მომსახურების გადასახადი დასაფარია.';

            return;
          }

          this.generalError =
            'დომენის შემოწმება ვერ მოხერხდა. სცადე ხელახლა.';
        },
      });
  }





  private handleSignupError(
    apiError: any,
  ): void {
    const apiErrors =
      apiError?.error?.errors;

    this.error =
      apiErrors &&
      typeof apiErrors === 'object'
        ? apiErrors
        : {};
    
    if (this.error['subdomain']) {
      this.error['sub_domain'] =
        this.error['subdomain'];

      delete this.error['subdomain'];
    }

    if (this.error['password']) {
      this.passwordErrors =
        this.parsePasswordErrors(
          this.error['password'],
        );
    }

    this.generalError =
      apiError?.error?.message ||
      'რეგისტრაცია ვერ დასრულდა. შეამოწმე მონაცემები და სცადე ხელახლა.';

    this.markApiErrorControlsAsTouched();

    this.scrollToFirstInvalidControl();
  }


  private markApiErrorControlsAsTouched(): void {
    Object.keys(this.error)
      .forEach((fieldName: string) => {
        this.validateForm
          .get(fieldName)
          ?.markAsTouched();
      });
  }


  private calculatePasswordStrength(
    password: string,
  ): void {
    let strength = 0;

    if (password.length >= 8) {
      strength += 1;
    }

    if (/[a-z]/.test(password)) {
      strength += 1;
    }

    if (/[A-Z]/.test(password)) {
      strength += 1;
    }

    if (/\d/.test(password)) {
      strength += 1;
    }

    if (/[\W_]/.test(password)) {
      strength += 1;
    }

    this.passwordStrength =
      (strength / 5) * 100;
  }


  private getLocalPasswordErrors(): string[] {
    const passwordControl =
      this.validateForm.get('password');

    if (!passwordControl?.errors) {
      return [];
    }

    const errors: string[] = [];

    if (
      passwordControl.hasError('required')
    ) {
      errors.push(
        'validation.PASSWORD_REQUIRED',
      );
    }

    if (
      passwordControl.hasError('minlength')
    ) {
      errors.push(
        'validation.PASSWORD_MIN',
      );
    }

    if (!this.hasLowercaseLetter) {
      errors.push(
        'validation.PASSWORD_LOWERCASE',
      );
    }

    if (!this.hasPasswordNumber) {
      errors.push(
        'validation.PASSWORD_NUMBER',
      );
    }

    return [...new Set(errors)];
  }


  private parsePasswordErrors(
    passwordErrors: unknown,
  ): string[] {
    const firstError =
      Array.isArray(passwordErrors)
        ? passwordErrors[0]
        : passwordErrors;

    if (
      typeof firstError !== 'string'
    ) {
      return this.getLocalPasswordErrors();
    }

    return firstError
      .split(' ')
      .filter(Boolean);
  }


  private normalizeSubDomain(
    value: string,
  ): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-{2,}/g, '-');
  }


  private isSubDomainFormatValid(
    subDomain: string,
  ): boolean {
    return /^(?!-)(?!.*--)[a-z0-9-]{2,63}(?<!-)$/
      .test(subDomain);
  }


  private resetDomainState(): void {
    this.checkingDomain = false;
    this.domainChecked = false;
    this.isDomainTaken = false;
    this.isDomainValid = false;
    this.generalError = '';
  }


  private removeApiError(
    fieldName: string,
  ): void {
    if (
      Object.prototype.hasOwnProperty.call(
        this.error,
        fieldName,
      )
    ) {
      delete this.error[fieldName];
    }
  }


  private scrollToFirstInvalidControl(): void {
    window.setTimeout(() => {
      const firstInvalidElement =
        document.querySelector(
          '.is-invalid, .ml-register-form__error',
        );

      firstInvalidElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 50);
  }
}